import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import { Card, Chip, Btn } from "../components/ui";

type Column = { name: string; type: string };

export default function ExploreScreen() {
  const [tables, setTables] = useState<string[]>([]);
  const [table, setTable] = useState<string | null>(null);
  const [schema, setSchema] = useState<{ columns: Column[]; rowCount: number; lastWrite: string | null } | null>(null);
  const [sql, setSql] = useState("SELECT * FROM sites LIMIT 20;");
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ tables: string[] }>("/api/admin/explore/tables").then((r) => {
      setTables(r.tables);
      if (r.tables.length) selectTable(r.tables.includes("sites") ? "sites" : r.tables[0]);
    });
  }, []);

  function selectTable(t: string) {
    setTable(t);
    apiGet<{ columns: Column[]; rowCount: number; lastWrite: string | null }>(
      `/api/admin/explore/schema?table=${encodeURIComponent(t)}`,
    ).then(setSchema);
  }

  async function runQuery(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setQueryError(null);
    try {
      const r = await apiPost<{ rows: Record<string, unknown>[] }>("/api/admin/explore/query", { sql });
      setRows(r.rows);
    } catch (e: any) {
      setQueryError(String(e.message ?? e));
      setRows(null);
    } finally {
      setBusy(false);
    }
  }

  async function saveTool(e: React.FormEvent) {
    e.preventDefault();
    setSaveMsg(null);
    try {
      await apiPost("/api/admin/custom-tools", { name: saveName, description: saveDesc, sql });
      setSaveMsg("Saved. It appears as an MCP tool once task 8's custom-tool wiring reads it.");
      setSaveOpen(false);
      setSaveName("");
      setSaveDesc("");
    } catch (e: any) {
      setSaveMsg(String(e.message ?? e));
    }
  }

  function exportCsv() {
    if (!rows || !rows.length) return;
    const cols = Object.keys(rows[0]);
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "explore-result.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl text-ink">Explore</h1>
      </div>
      <div className="flex flex-wrap gap-2">
        {tables.slice(0, 12).map((t) => (
          <Chip key={t} on={t === table} onClick={() => selectTable(t)}>
            {t}
          </Chip>
        ))}
        {tables.length > 12 && <Chip>+ {tables.length - 12} more</Chip>}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3.5 lg:grid-cols-[1.5fr_1fr]">
        <div className="flex flex-col gap-3.5">
          <Card>
            <form onSubmit={runQuery} className="flex flex-col gap-2.5">
              <textarea
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                rows={5}
                spellCheck={false}
                className="w-full rounded-lg border border-neutral-200 bg-neutral-50 p-3 font-mono text-[12.5px] text-ink outline-none focus:border-brand"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Btn type="submit" disabled={busy}>
                  {busy ? "Running…" : "Run"}
                </Btn>
                <Sub>SELECT only · 1,000-row cap · 5s timeout</Sub>
              </div>
            </form>
          </Card>

          <Card>
            {queryError && <p className="text-sm text-brand">{queryError}</p>}
            {rows && <ResultTable rows={rows} />}
            {rows && (
              <div className="mt-3.5 flex gap-2.5">
                <Btn onClick={() => setSaveOpen(true)}>Save as MCP tool</Btn>
                <Btn secondary onClick={exportCsv}>
                  Export CSV
                </Btn>
              </div>
            )}
            {saveMsg && <p className="mt-2 text-xs text-neutral-500">{saveMsg}</p>}
          </Card>
        </div>

        <Card>
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg">{table ?? "—"}</h2>
          </div>
          {schema && (
            <>
              <div className="overflow-x-auto">
              <table className="mt-3 w-full min-w-[280px] text-[13px]">
                <thead>
                  <tr>
                    <th className="border-b border-neutral-200 pb-2 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                      column
                    </th>
                    <th className="border-b border-neutral-200 pb-2 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                      type
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schema.columns.map((c: any) => (
                    <tr key={c.name}>
                      <td className="border-b border-neutral-100 py-2 text-ink">{c.name}</td>
                      <td className="border-b border-neutral-100 py-2 text-neutral-500">{c.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p className="mt-3.5 text-xs text-neutral-400">
                {schema.rowCount.toLocaleString()} rows
                {schema.lastWrite && <> · last write {schema.lastWrite}</>}
              </p>
            </>
          )}
        </Card>
      </div>

      {saveOpen && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30 p-4 sm:p-6" onClick={() => setSaveOpen(false)}>
          <form
            onSubmit={saveTool}
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-card bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg text-ink">Save as MCP tool</h3>
            <label className="mt-3 flex flex-col gap-1 text-sm font-medium text-neutral-700">
              Name (lowercase_snake_case)
              <input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                required
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
              />
            </label>
            <label className="mt-3 flex flex-col gap-1 text-sm font-medium text-neutral-700">
              Description (what Claude sees)
              <input
                value={saveDesc}
                onChange={(e) => setSaveDesc(e.target.value)}
                required
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
              />
            </label>
            <div className="mt-4 flex gap-2.5">
              <Btn type="submit">Save</Btn>
              <Btn secondary onClick={() => setSaveOpen(false)}>
                Cancel
              </Btn>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-neutral-400">{children}</span>;
}

function ResultTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return <p className="text-sm text-neutral-400">No rows.</p>;
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                className="border-b border-neutral-200 px-2.5 pb-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c} className="border-b border-neutral-100 px-2.5 py-2 text-ink">
                  {String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 200 && <p className="mt-2 text-xs text-neutral-400">Showing first 200 of {rows.length} rows.</p>}
    </div>
  );
}
